import styled, { css } from 'styled-components';

import { ArrayResponsiveProp, ResponsiveProp, SimpleProp } from '../../theme/helpers';
import { CheckboxLabel } from './Checkbox';
import { RadioLabel } from './Radio';
import { SelectInput } from './Select';
import { UIProps } from '../../theme/mixins';
import Input from './Input';
import SC from './SC';

export const TD = styled.td<SC<UIProps>>`
  ${({ sc: props = {}, theme }) => css`
    padding: .2rem 0;
    vertical-align: top;

    ${theme.useMixins(props)}
  `}
`;

interface TRProps {
  borderTop?: number;
}

export const TR = styled.tr<SC<UIProps & TRProps>>`
  ${({ sc: { borderTop, ...props } = {}, theme }) => css`
    ${borderTop && css`
      > td {
        border-top: ${borderTop}px solid rgba(0, 0, 0, .05);
      }
    `}

    ${theme.useMixins(props)}
  `}
`;

export const THead = styled.thead<SC<UIProps>>`
  ${({ sc: props = {}, theme }) => css`
    ${TH} {
      padding: .4rem 0;
      font-size: ${theme.fontSizes.body}rem;
      color: ${theme.colors.gray[500]};
      font-weight: 500;
      text-align: left;
    }

    ${theme.useMixins(props)}
  `}
`;

export const TH = styled.th<SC<UIProps>>`
  ${({ sc: props = {}, theme }) => css`
    ${theme.useMixins(props)}
  `}
`;

export const TBody = styled.tbody<SC<UIProps>>`
  ${({ sc: props = {}, theme }) => css`
    ${theme.useMixins(props)}
  `}
`;

export interface TableProps {
  spacingV?: SimpleProp<number> | ResponsiveProp<number> | ArrayResponsiveProp<number>;
  borderedRows?: boolean;
  borderWidth?: number;
  bordered?: boolean;
}

export const Table = styled.table<SC<TableProps & UIProps>>`
  ${({ sc: {
    spacingV = 0.5, borderedRows = false, borderWidth = 1, bordered, ...props
  } = {}, theme }) => css`
    width: 100%;

    ${borderedRows && css`
      ${TBody} {
        border-top: ${borderWidth}px solid rgba(0, 0, 0, .05);
      }

      ${TR} + ${TR} {
        border-top: ${borderWidth}px solid rgba(0, 0, 0, .05);
      }

      ${TR}:last-child {
        border-bottom: ${borderWidth}px solid rgba(0, 0, 0, .05);
      }
    `}

    ${!!spacingV && theme.responsiveProp(spacingV, (spacing: number) => css`
      ${TD} {
        padding-top: ${(spacing / 4) * theme.gutter}px;
        padding-bottom: ${(spacing / 4) * theme.gutter}px;
      }
    `)}

    ${bordered && css`
      ${TD}, ${TH} {
        border: ${borderWidth}px solid rgba(0, 0, 0, .075);
        padding: ${theme.gutter / 4}px;
      }
    `}

    ${theme.useMixins(props)}
  `}
`;

export const FormTable = styled.table<SC<UIProps>>`
  ${({ theme, ...props }) => css`
    width: 100%;
    border: 1px solid ${theme.colors.gray[250]};
    border-collapse: separate;
    border-spacing: 0;
    border-radius: ${theme.borderRadiuses.md}px;
    table-layout: fixed;

    td {
      padding: 0;
    }

    th, td {
      border-right: 1px solid ${theme.colors.gray[250]};
      border-bottom: 1px solid ${theme.colors.gray[250]};
    }

    th:last-child, td:last-child {
      border-right: none;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    ${SelectInput}, ${Input}, .react-datepicker__input-container > input {
      border: none !important;
      border-radius: 0 !important;;
    }

    ${CheckboxLabel}, ${RadioLabel} {
      height: 38px;
      line-height: 38px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      padding-right: 10px;
      padding-left: 34px;

      > span:first-of-type {
        left: 10px;
        top: 14px;
      }
    }

    tr:first-child {
      td:first-child {
        ${SelectInput}, ${Input}, .react-datepicker__input-container > input {
          border-top-left-radius: ${theme.borderRadiuses.md}px !important;
        }
      }

      td:last-child {
        ${SelectInput}, ${Input}, .react-datepicker__input-container > input {
          border-top-right-radius: ${theme.borderRadiuses.md}px !important;
        }
      }
    }

    tr:last-child {
      td:first-child {
        ${SelectInput}, ${Input}, .react-datepicker__input-container > input {
          border-bottom-left-radius: ${theme.borderRadiuses.md}px !important;
        }
      }

      td:last-child {
        ${SelectInput}, ${Input}, .react-datepicker__input-container > input {
          border-bottom-right-radius: ${theme.borderRadiuses.md}px !important;
        }
      }
    }

    ${theme.useMixins(props)}
  `}
`;
